/* eslint-disable */
import { RolePermission } from "api/role/addRole";

export interface RoleFlatItem {
  role: string;
  depth: number;
  parentRole: string | null;
  isDefault: boolean;
  userCount: number;
  permissions: any;
  children: RolePermission[];
}

/**
 * Build a tree structure from a flat array of roles
 * Each role node will have a .children array
 */
export const buildTree = (roles: RolePermission[]): RolePermission[] => {
  const rolesMap = new Map<string, RolePermission & { children: RolePermission[] }>();

  // Initialize map with all roles
  roles.forEach((role) => {
    rolesMap.set(role.role, { ...role, children: [] });
  });

  // Build parent-child relationships
  const roots: RolePermission[] = [];
  rolesMap.forEach((role) => {
    if (role.parentRole && rolesMap.has(role.parentRole)) {
      rolesMap.get(role.parentRole)!.children.push(role as RolePermission);
    } else {
      roots.push(role as RolePermission);
    }
  });

  return roots;
};

/**
 * Flatten a tree structure to a single-level array in DFS order
 * Each item includes its depth level
 */
export const flattenTree = (roots: RolePermission[]): RoleFlatItem[] => {
  const result: RoleFlatItem[] = [];

  const traverse = (
    role: RolePermission & { children?: RolePermission[] },
    depth: number
  ) => {
    result.push({
      role: role.role,
      depth,
      parentRole: role.parentRole || null,
      isDefault: role.isDefault || false,
      userCount: (role as RolePermission & { userCount?: number }).userCount || 0,
      permissions: role.permissions,
      children: role.children || [],
    });

    if (role.children) {
      role.children.forEach((child) =>
        traverse(child as RolePermission & { children?: RolePermission[] }, depth + 1)
      );
    }
  };

  roots.forEach((root) => traverse(root as RolePermission & { children?: RolePermission[] }, 0));
  return result;
};

/**
 * Compute new parent after drag and drop
 * Algorithm:
 * 1. If dropped at index 0 → parentRole = null (becomes root)
 * 2. Else → parentRole = role at (newIndex - 1) (becomes child of item above)
 */
export const computeNewParent = (
  flatItems: RoleFlatItem[],
  draggedIndex: number,
  destinationIndex: number
): string | null => {
  // Create a working copy
  const items = [...flatItems];
  const draggedItem = items[draggedIndex];

  // Remove dragged item from array
  items.splice(draggedIndex, 1);

  // If dropped at top (index 0), becomes root
  if (destinationIndex === 0) {
    return null;
  }

  // Get the item that was before the dropped position
  // destinationIndex is now relative to the array without the dragged item
  const insertAfterIndex = destinationIndex > draggedIndex
    ? destinationIndex - 1
    : destinationIndex;

  const itemAbove = items[insertAfterIndex - 1];

  if (!itemAbove) {
    return null;
  }

  // Dropped item becomes a child of the item above it
  return itemAbove.role;
};

/**
 * Filter flat items to show only those whose ancestor chain is expanded
 * @param flatItems - The flat list of items
 * @param expandedRoles - Set of role names that are expanded
 */
export const filterVisibleItems = (
  flatItems: RoleFlatItem[],
  expandedRoles: Set<string>
): RoleFlatItem[] =>
  flatItems.filter((item) => {
    if (item.depth === 0) return true;

    // Check if all ancestors are expanded
    let currentRole: string | null = item.parentRole;
    while (currentRole) {
      if (!expandedRoles.has(currentRole)) {
        return false;
      }
      const parentItem = flatItems.find((r) => r.role === currentRole);
      currentRole = parentItem?.parentRole || null;
    }

    return true;
  });
